/**
 * SchoolPage — Thin orchestrator for the KVS Samagam Schools page.
 *
 * All heavy lifting lives in extracted hooks & components:
 *   - useFilters   → filter state & handlers
 *   - useKPIData   → data fetching, processing, derived values
 *   - FilterBar    → dropdown filters + quick filter chips
 *   - TabNavigation, MapTab, ChartTab, TabularTab → tab content
 *
 * This file only manages:
 *   1. Page layout (Header, HeroCarousel, Breadcrumb, Footer)
 *   2. Tab state + map mode state
 *   3. Map-resize side effect on tab switch
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';

import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/school.png';

import { useFilters } from '../hooks/useFilters';
import { useKPIData } from '../hooks/useKPIData';
import FilterBar from '../components/FilterBar';
import { TabNavigation, MapTab, ChartTab, TabularTab } from '../components/tabs';
import AdvancedFilter, { FilterCondition } from '../components/AdvancedFilter';
import type { ActiveTab } from '../types';

// ─── Carousel images for the hero banner ─────────────────────
const carouselImages = [img1];

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function SchoolPage() {
  const navigate = useNavigate();

  // ── Local UI state (not filter-related) ────────────────────
  const [activeTab, setActiveTab] = useState<ActiveTab>('map');
  const [showDistricts, setShowDistricts] = useState(false);
  const [mapMode, setMapMode] = useState<'state' | 'region'>('state');

  // ── Compose hooks ──────────────────────────────────────────
  const filters = useFilters();

  const kpiData = useKPIData({
    state: filters.state,
    region: filters.region,
    category: filters.category,
    kpi: filters.kpi,
    selectedInfraItems: filters.selectedInfraItems,
    globalFilters: filters.globalFilters,
    activeQuickFilters: filters.activeQuickFilters,
    setFilterConfigs: filters.setFilterConfigs,
  });

  // ── Trigger map resize when switching back to map tab ──────
  useEffect(() => {
    if (activeTab === 'map') {
      const timers = [
        setTimeout(() => window.dispatchEvent(new Event('resize')), 50),
        setTimeout(() => window.dispatchEvent(new Event('resize')), 150),
        setTimeout(() => window.dispatchEvent(new Event('resize')), 300),
      ];
      return () => timers.forEach((t) => clearTimeout(t));
    }
  }, [activeTab]);

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col bg-[#F5F5F5] overflow-x-hidden">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full">
        <Breadcrumb />
      </div>

      {/* Advanced Filter Modal — always mounted so it works from Map, Chart, and Tabular tabs */}
      <AdvancedFilter
        filterConfigs={filters.filterConfigs}
        onFilterChange={(f: FilterCondition[]) => { filters.setGlobalFilters(f); filters.setSavedFilters(f); }}
        initialFilters={filters.globalFilters}
        open={filters.filterModalOpen}
        onClose={() => filters.setFilterModalOpen(false)}
        selectedKpi={filters.kpi}
        filtersEnabled={filters.filtersEnabled}
        onFiltersEnabledChange={enabled => {
          if (enabled) filters.setGlobalFilters(filters.savedFilters);
          else { filters.setSavedFilters(filters.globalFilters); filters.setGlobalFilters([]); }
          filters.setFiltersEnabled(enabled);
        }}
      />

      <main className="flex-grow px-10 w-full flex flex-col">
        {/* ── Page header + Analytics Dashboard button ─────── */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-[#6A1786] pl-2">Schools</h1>
          <Button
            variant="contained"
            onClick={() => navigate('/kvs-samagam/analytics')}
            startIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            }
            sx={{
              backgroundColor: '#6A1786',
              '&:hover': { backgroundColor: '#481789' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Analytics Dashboard
          </Button>
        </div>

        {/* ── Filter Bar ──────────────────────────────────── */}
        <FilterBar filters={filters} kpiData={kpiData} />

        {/* ── Dashboard content ───────────────────────────── */}
        <div className="w-full bg-white rounded-lg shadow-sm relative">
          {/* Tab navigation + global filter controls */}
          <TabNavigation
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            filters={filters}
            kpiData={kpiData}
          />

          {/* Tab content */}
          <div className="p-6">
            {activeTab === 'map' && (
              <MapTab
                filters={filters}
                kpiData={kpiData}
                mapMode={mapMode}
                setMapMode={setMapMode}
                showDistricts={showDistricts}
              />
            )}

            {activeTab === 'chart' && (
              <ChartTab filters={filters} kpiData={kpiData} />
            )}

            {activeTab === 'tabular' && (
              <TabularTab filters={filters} kpiData={kpiData} />
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
